import React, { useEffect, useState, useCallback, useMemo } from "react";
import { IoArrowBackOutline } from "react-icons/io5";
import { FaCheckCircle, FaSort, FaDatabase, FaIoxhost } from "react-icons/fa";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { useGetDatasourceQuery } from "./sql/TablesourceApiSlice";
import { RiAdminFill } from "react-icons/ri";
import DropDownComp from "../../components/DropDownComp";
import { useDispatch, useStore } from "react-redux";
import {
  setDataSource,
  setWorkbookData,
  setInitialState,
} from "../reduxworkbook/WorkbookSlice";
import NewDataSources from "./NewDataSources";
import useAuth from "../../hooks/useAuth";
import {
  useCreateSheetMutation,
  useGetSearchDataSourcesQuery,
} from "./generalApiSlice";
import debounce from "lodash/debounce";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ConnectsqlForm } from "./ConnectsqlForm";
import { Connectionpgform } from "./Connectionpgform";
import { IoMdClose } from "react-icons/io";
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from "react-icons/md";
import LoadingIndicator from "../../components/BasicComponents/LoadingIndicator";
import useTitle from "../../hooks/useTitle";

export default function Datasource() {
  useTitle("Vizta Datasources");

  const GRID_COLS =
    "grid grid-cols-[1.2fr_1.2fr_1fr_1.2fr_1.4fr_40px] items-center";

  const [showPopup, setShowPopup] = useState({ isOpen: false, type: "" });
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [selectedDataSourceID, setSelectedDataSourceID] = useState();
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortedData, setSortedData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [openGroups, setOpenGroups] = useState(new Set());

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();
  const { roles } = useAuth();

  const {
    data: searchedData,
    isError: isSearchError,
    error: searchError,
  } = useGetSearchDataSourcesQuery(
    { value: debouncedSearchTerm },
    { skip: !debouncedSearchTerm }
  );

  const {
    data: tempDatasourcePgData = [],
    error: tempDatasourcePgError,
    isSuccess: isTempDatasourcePgSuccess,
    isLoading,
    isError,
  } = useGetDatasourceQuery();

  const [
    createSheet,
    {
      data,
      isSuccess: isCreateSheetSuccess,
      isLoading: isCreateSheetLoading,
      isError: isCreateSheetError,
      error: createSheeterror,
    },
  ] = useCreateSheetMutation();

  useEffect(() => {
    const mergeAndSort = () => {
      let combined = [];

      if (isTempDatasourcePgSuccess) {
        combined = [...combined, ...tempDatasourcePgData];
      }

      const normalized = combined.map((item) => ({
        ...item,
        id: item.id || item._id,
      }));

      if (sortBy === "AddedOn") {
        normalized.sort((a, b) => {
          const dateA = new Date(a[sortBy]);
          const dateB = new Date(b[sortBy]);

          return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
        });
      }

      setSortedData(normalized);
    };

    mergeAndSort();
  }, [isTempDatasourcePgSuccess, tempDatasourcePgData, sortBy, sortOrder]);

  const items = debouncedSearchTerm
    ? Array.isArray(searchedData)
      ? searchedData
      : []
    : sortedData;

  const groupedByDatabase = useMemo(() => {
    const map = new Map();

    items.forEach((connection) => {
      const dbName = connection.DataBaseName || "Unknown";

      if (!map.has(dbName)) map.set(dbName, []);
      map.get(dbName).push(connection);
    });

    return map;
  }, [items]);

  const toggleGroup = (dbName) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);

      next.has(dbName) ? next.delete(dbName) : next.add(dbName);

      return next;
    });
  };

  useEffect(() => {
    if (isCreateSheetSuccess && data) {
      const workbookId = data?.populatedWorkbook?._id;

      dispatch(setWorkbookData(data.populatedWorkbook));

      if (workbookId) {
        navigate(`/workbook?workbookid=${workbookId}`);
      }
    }
  }, [isCreateSheetSuccess, data, navigate, dispatch]);

  const handleConnect = () => {
    dispatch(setInitialState());

    const workBook = store.getState().Workbook.present;

    sessionStorage.setItem("workBookId", workBook._id);

    createSheet(workBook);
  };

  const handleRowSelect = (connection) => {
    const id = connection.id;

    setSelectedConnectionId(id);
    setSelectedDataSourceID(id);

    sessionStorage.setItem("dataSourceID", id);

    dispatch(setDataSource({ dataSourceID: id, initial: true }));
  };

  if (isCreateSheetError && createSheeterror.status === 400) {
    dispatch(
      setDataSource({
        dataSourceID: selectedDataSourceID,
        initial: true,
      })
    );

    handleConnect();
  }

  const handleSort = () => {
    if (sortBy === "AddedOn") {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy("AddedOn");
      setSortOrder("asc");
    }
  };

  const debouncedSetSearchTerm = useCallback(
    debounce((value) => setDebouncedSearchTerm(value), 300),
    []
  );

  const handleSearchColumn = (e) => {
    const value = e.target.value;

    setSearchTerm(value);
    debouncedSetSearchTerm(value);
  };

  let tableContent = null;

  if (isLoading) {
    tableContent = <LoadingIndicator />;
  } else if (isError || isSearchError) {
    tableContent = (
      <div className="p-4 text-red-500">
        Error while fetching datasources:{" "}
        {tempDatasourcePgError?.message || searchError?.message}
      </div>
    );
  } else if (groupedByDatabase.size > 0) {
    tableContent = (
      <div className="flex flex-col overflow-y-auto">
        {[...groupedByDatabase.entries()].map(([dbName, connections]) => {
          const isOpen = openGroups.has(dbName);

          return (
            <React.Fragment key={dbName}>
              <div
                className="flex items-center gap-2 font-semibold text-blue-950 px-4 py-2 border-b bg-blue-100 cursor-pointer hover:bg-blue-200 transition-colors"
                onClick={() => toggleGroup(dbName)}
              >
                {isOpen ? (
                  <MdKeyboardArrowDown className="text-lg shrink-0" />
                ) : (
                  <MdKeyboardArrowRight className="text-lg shrink-0" />
                )}

                <FaDatabase className="shrink-0" />

                <span>{dbName}</span>

                <span className="ml-2 text-xs text-gray-500 font-normal">
                  ({connections.length}{" "}
                  {connections.length === 1 ? "source" : "sources"})
                </span>
              </div>

              {isOpen &&
                connections.map((connection, i) => (
                  <div
                    key={connection.id || i}
                    className={`${GRID_COLS} cursor-pointer border-t ${selectedConnectionId === connection.id
                        ? "bg-blue-950 text-white"
                        : i % 2 === 0
                          ? "bg-white text-black"
                          : "bg-gray-100 text-black"
                      }`}
                    onClick={() => handleRowSelect(connection)}
                    onDoubleClick={handleConnect}
                  >
                    <div className="px-3 py-2 truncate">
                      {connection.NickName}
                    </div>

                    <div className="px-3 py-2 truncate">
                      {connection.DataBaseName}
                    </div>

                    <div className="px-3 py-2 truncate">
                      {connection.Owner}
                    </div>

                    <div className="px-3 py-2 truncate">
                      {connection.Host}
                    </div>

                    <div className="px-3 py-2 truncate">
                      {connection.AddedOn
                        ? format(
                          new Date(connection.AddedOn),
                          "MMMM dd, yyyy h:mm a"
                        )
                        : ""}
                    </div>

                    <div />
                  </div>
                ))}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <>
      <section className="flex flex-col overflow-hidden overflow-y-auto h-screen">
        <header className="w-full h-[5%] gap-2 flex items-center justify-between bg-blue-950 text-white px-2 text-xl">
          <button
            className="flex items-center gap-1"
            onClick={() => navigate("/home/dash")}
          >
            <IoArrowBackOutline />
            <strong>Home</strong>
          </button>

          <DropDownComp />
        </header>

        <section
          className={`flex flex-col gap-2 items-start w-full pl-[1.9%] ${roles.includes("Explorer") ? "h-[92.5%]" : "h-[70%]"
            }`}
        >
          <h2 className="w-full text-left text-2xl font-medium">
            Saved Data Sources
          </h2>

          <div className="flex items-center w-full relative -left-[1%]">
            <FaMagnifyingGlass className="relative left-5 text-md text-gray-200" />

            <input
              type="text"
              placeholder="search the data sources..."
              onChange={handleSearchColumn}
              value={searchTerm}
              className="w-[18%] border-2 rounded px-5 text-black outline-none"
            />
          </div>

          <div className="w-[90%] flex justify-end items-center">
            {isCreateSheetLoading && <LoadingIndicator />}

            {isCreateSheetError && createSheeterror.data?.message}

            <button
              className={`border rounded py-1 px-2 font-medium ${selectedConnectionId !== null
                  ? "bg-blue-950 text-white"
                  : "bg-gray-200 text-black"
                }`}
              disabled={selectedConnectionId === null}
              onClick={handleConnect}
            >
              Connect
            </button>
          </div>

          <div
            className={`shadow-xl border-t-2 border-b w-[90%] ${roles.includes("Explorer") ? "h-full" : "h-[70%]"
              } rounded-lg border-gray-900 flex flex-col overflow-hidden`}
          >
            <div
              className={`${GRID_COLS} bg-gray-50 border-b text-xs font-medium text-gray-500`}
            >
              <div className="px-3 py-2 flex items-center gap-1">
                <FaCheckCircle size={12} />
                Nickname
              </div>

              <div className="px-3 py-2 flex items-center gap-1">
                <FaDatabase size={12} />
                Database
              </div>

              <div className="px-3 py-2 flex items-center gap-1">
                <RiAdminFill size={12} />
                Owner
              </div>

              <div className="px-3 py-2 flex items-center gap-1">
                <FaIoxhost size={12} />
                Host
              </div>

              <div
                className="px-3 py-2 flex items-center gap-1 cursor-pointer"
                onClick={handleSort}
              >
                <FaSort size={12} />
                Added on
              </div>

              <div />
            </div>

            {tableContent}
          </div>
        </section>

        {!roles.includes("Explorer") && (
          <NewDataSources setShowPopup={setShowPopup} />
        )}
      </section>

      {showPopup.isOpen && (
        <main className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <section className="w-[22rem] h-auto bg-white flex flex-col items-center rounded">
            <header className="flex w-full items-center justify-between bg-blue-950 px-2 py-1 text-white">
              {showPopup.type}

              <IoMdClose
                onClick={() => setShowPopup({ isOpen: false, type: "" })}
                className="cursor-pointer"
              />
            </header>

            {showPopup.type === "MySQL" && (
              <ConnectsqlForm
                closeModal={() => setShowPopup({ isOpen: false, type: "" })}
              />
            )}

            {showPopup.type === "POSTGRESQL" && (
              <Connectionpgform
                closeModal={() => setShowPopup({ isOpen: false, type: "" })}
              />
            )}
          </section>
        </main>
      )}
    </>
  );
}